import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Calendar, Clock, Globe2, AlarmClock, MapPin } from "lucide-react";

const TIMEZONE_REGIONS = {
  "Americas": [
    { value: "America/Los_Angeles", label: "Los Angeles (PT)", offset: "UTC-7/8" },
    { value: "America/Denver", label: "Denver (MT)", offset: "UTC-6/7" },
    { value: "America/Chicago", label: "Chicago (CT)", offset: "UTC-5/6" },
    { value: "America/New_York", label: "New York (ET)", offset: "UTC-4/5" },
    { value: "America/Toronto", label: "Toronto (ET)", offset: "UTC-4/5" },
    { value: "America/Mexico_City", label: "Mexico City", offset: "UTC-5/6" },
    { value: "America/Bogota", label: "Bogotá", offset: "UTC-5" },
    { value: "America/Sao_Paulo", label: "São Paulo", offset: "UTC-3" },
    { value: "America/Argentina/Buenos_Aires", label: "Buenos Aires", offset: "UTC-3" },
  ],
  "Europe": [
    { value: "Europe/London", label: "London (GMT/BST)", offset: "UTC+0/1" },
    { value: "Europe/Paris", label: "Paris (CET)", offset: "UTC+1/2" },
    { value: "Europe/Berlin", label: "Berlin (CET)", offset: "UTC+1/2" },
    { value: "Europe/Madrid", label: "Madrid (CET)", offset: "UTC+1/2" },
    { value: "Europe/Amsterdam", label: "Amsterdam (CET)", offset: "UTC+1/2" },
    { value: "Europe/Rome", label: "Rome (CET)", offset: "UTC+1/2" },
    { value: "Europe/Stockholm", label: "Stockholm (CET)", offset: "UTC+1/2" },
    { value: "Europe/Athens", label: "Athens (EET)", offset: "UTC+2/3" },
    { value: "Europe/Moscow", label: "Moscow", offset: "UTC+3" },
  ],
  "Asia & Middle East": [
    { value: "Asia/Dubai", label: "Dubai (GST)", offset: "UTC+4" },
    { value: "Asia/Karachi", label: "Karachi (PKT)", offset: "UTC+5" },
    { value: "Asia/Kolkata", label: "Mumbai / Delhi (IST)", offset: "UTC+5:30" },
    { value: "Asia/Dhaka", label: "Dhaka", offset: "UTC+6" },
    { value: "Asia/Bangkok", label: "Bangkok (ICT)", offset: "UTC+7" },
    { value: "Asia/Singapore", label: "Singapore (SGT)", offset: "UTC+8" },
    { value: "Asia/Hong_Kong", label: "Hong Kong", offset: "UTC+8" },
    { value: "Asia/Shanghai", label: "Shanghai (CST)", offset: "UTC+8" },
    { value: "Asia/Seoul", label: "Seoul (KST)", offset: "UTC+9" },
    { value: "Asia/Tokyo", label: "Tokyo (JST)", offset: "UTC+9" },
  ],
  "Africa": [
    { value: "Africa/Lagos", label: "Lagos (WAT)", offset: "UTC+1" },
    { value: "Africa/Nairobi", label: "Nairobi (EAT)", offset: "UTC+3" },
    { value: "Africa/Johannesburg", label: "Johannesburg (SAST)", offset: "UTC+2" },
    { value: "Africa/Cairo", label: "Cairo (EET)", offset: "UTC+2" },
  ],
  "Oceania": [
    { value: "Australia/Perth", label: "Perth (AWST)", offset: "UTC+8" },
    { value: "Australia/Sydney", label: "Sydney (AEST)", offset: "UTC+10/11" },
    { value: "Australia/Melbourne", label: "Melbourne (AEST)", offset: "UTC+10/11" },
    { value: "Pacific/Auckland", label: "Auckland (NZST)", offset: "UTC+12/13" },
  ],
  "Other": [
    { value: "UTC", label: "UTC (Coordinated Universal Time)", offset: "UTC+0" },
  ],
};

const ALL_TZS = Object.values(TIMEZONE_REGIONS).flat();

const pad = (n) => String(n).padStart(2, "0");

function defaultIsoTomorrow9amUtc() {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + 1);
  d.setUTCHours(9, 0, 0, 0);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

function defaultDateTomorrow() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function localUtcInputToIso(value) {
  if (!value) return null;
  return `${value}:00Z`;
}

const ModeCard = ({ id, currentMode, onSelect, icon: Icon, title, sub }) => (
  <button
    type="button"
    onClick={() => onSelect(id)}
    data-testid={`schedule-mode-${id}`}
    className="p-4 text-left border transition-all"
    style={{
      borderColor: currentMode === id ? "var(--cf-text)" : "var(--cf-border)",
      background: currentMode === id ? "var(--cf-highlight)" : "var(--cf-surface)",
    }}
  >
    <div className="flex items-center gap-2 mb-1">
      <Icon size={13} style={{ color: "var(--cf-accent)" }} />
      <span className="font-mono text-[10px] uppercase tracking-widest cf-text-base font-semibold">{title}</span>
    </div>
    <div className="text-[11px] cf-text-dim leading-snug">{sub}</div>
  </button>
);

export default function ScheduleCampaignDialog({ open, onOpenChange, onSchedule, initial = null, busy = false }) {
  const [mode, setMode] = useState(initial?.mode || "tz_aware");
  const [scheduledAt, setScheduledAt] = useState(
    initial?.scheduled_at ? initial.scheduled_at.slice(0, 16) : defaultIsoTomorrow9amUtc()
  );
  const [scheduledDate, setScheduledDate] = useState(initial?.scheduled_date || defaultDateTomorrow());
  const [sendLocalTime, setSendLocalTime] = useState(initial?.send_local_time || "09:00");
  const [defaultTz, setDefaultTz] = useState(initial?.default_timezone || "UTC");

  const browserTz = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; }
    catch { return "UTC"; }
  }, []);

  const selectedTzInfo = useMemo(() => {
    return ALL_TZS.find((t) => t.value === defaultTz) || { value: defaultTz, label: defaultTz, offset: "" };
  }, [defaultTz]);

  const submit = () => {
    const payload = {
      mode,
      scheduled_at: (mode === "datetime" || mode === "both") ? localUtcInputToIso(scheduledAt) : null,
      scheduled_date: (mode === "tz_aware" || mode === "both") ? scheduledDate : null,
      send_local_time: (mode === "tz_aware" || mode === "both") ? sendLocalTime : null,
      default_timezone: defaultTz || "UTC",
    };
    onSchedule?.(payload);
  };

  const modes = [
    { id: "datetime", icon: Calendar, title: "Specific UTC time", sub: "Send to everyone at one moment (UTC)." },
    { id: "tz_aware", icon: Globe2, title: "Recipient timezone", sub: "Each contact receives at their local time." },
    { id: "both", icon: AlarmClock, title: "Both / floor + local", sub: "Local time, but never before a UTC floor." },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-none max-w-2xl"
        style={{ background: "var(--cf-surface)", borderColor: "var(--cf-border)", color: "var(--cf-text)" }}
        data-testid="schedule-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">
            Schedule Campaign
          </DialogTitle>
          <div className="font-mono text-[10px] uppercase tracking-widest cf-text-dim">
            // pick when step 1 dispatches — follow-ups remain relative
          </div>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Mode selection */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {modes.map((m) => (
              <ModeCard key={m.id} id={m.id} icon={m.icon} title={m.title} sub={m.sub} currentMode={mode} onSelect={setMode} />
            ))}
          </div>

          {/* UTC datetime */}
          {(mode === "datetime" || mode === "both") && (
            <div data-testid="schedule-datetime-fields">
              <label className="font-mono text-[10px] uppercase tracking-widest cf-text-dim block mb-2">
                {mode === "both" ? "Earliest UTC dispatch (floor)" : "Send at (UTC)"}
              </label>
              <div className="relative">
                <Clock size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--cf-text-dim)" }} />
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  className="cf-input"
                  style={{ paddingLeft: 34 }}
                  data-testid="schedule-datetime-input"
                />
              </div>
              <div className="font-mono text-[10px] cf-text-faint mt-1">
                Your browser timezone is <span className="cf-text-muted">{browserTz}</span>. The value above is interpreted as UTC.
              </div>
            </div>
          )}

          {/* Timezone-aware fields */}
          {(mode === "tz_aware" || mode === "both") && (
            <div data-testid="schedule-tz-fields">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-widest cf-text-dim block mb-2">
                    Date (recipient local)
                  </label>
                  <input type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} className="cf-input" data-testid="schedule-date-input" />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-widest cf-text-dim block mb-2">
                    Time (recipient local)
                  </label>
                  <input type="time" value={sendLocalTime} onChange={(e) => setSendLocalTime(e.target.value)} className="cf-input" data-testid="schedule-local-time-input" />
                </div>
              </div>

              {/* Region-based timezone picker */}
              <div className="mt-4">
                <label className="font-mono text-[10px] uppercase tracking-widest cf-text-dim block mb-2 flex items-center gap-2">
                  <MapPin size={11} /> Default timezone / region (for contacts without one)
                </label>
                <select
                  value={defaultTz}
                  onChange={(e) => setDefaultTz(e.target.value)}
                  className="cf-input"
                  data-testid="schedule-default-tz-select"
                >
                  {Object.entries(TIMEZONE_REGIONS).map(([region, zones]) => (
                    <optgroup key={region} label={`── ${region} ──`}>
                      {zones.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label} ({tz.offset})
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>

                {selectedTzInfo && (
                  <div className="mt-2 p-3 border" style={{ borderColor: "var(--cf-border)", background: "var(--cf-surface)" }}>
                    <div className="flex items-center gap-2">
                      <Globe2 size={12} style={{ color: "var(--cf-accent)" }} />
                      <span className="font-mono text-[10px] uppercase tracking-widest cf-text-dim">Selected region</span>
                    </div>
                    <div className="cf-text-base text-sm font-medium mt-1">{selectedTzInfo.label}</div>
                    <div className="font-mono text-[10px] cf-text-faint mt-0.5">{selectedTzInfo.offset} · Emails will arrive at {sendLocalTime || "09:00"} local time in this zone</div>
                  </div>
                )}

                <div className="font-mono text-[10px] cf-text-faint mt-2">
                  Contacts with a <span className="cf-text-muted">timezone</span> field (from CSV import) will receive emails at their own local time. The default above is used for contacts without a timezone.
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="pt-4 gap-2">
          <button type="button" onClick={() => onOpenChange(false)} className="cf-btn-secondary" data-testid="schedule-cancel">Cancel</button>
          <button type="button" onClick={submit} disabled={busy} className="cf-btn-primary" data-testid="schedule-confirm">
            {busy ? "Scheduling…" : "Schedule Campaign"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
